import apiClient from './axios';
import { ApiResponse } from '../types';
import { LedgerRepairView } from './business-accounts';

/**
 * A person's history against what Safe Haven took.
 *
 * The ledger repairs used to be reachable only from a business account, so
 * a consumer's airtime recorded at ₦100 when Safe Haven took ₦97 had nowhere
 * to be looked at. This is the people side: one person's airtime and bills,
 * and the estate-wide repairs that put people right. History only — nothing
 * here moves money.
 */

export interface PersonPurchase {
  id: string;
  kind: 'airtime' | 'bill';
  label: string;
  category: string | null;
  reference: string;
  status: string;
  /** Face value. */
  amount: number;
  /** What the person was recorded as paying; null on old rows (face value). */
  charged: number | null;
  /** Collected by PayIQ — face value is then right. */
  commission: number;
  balanceBefore: number | null;
  balanceAfter: number | null;
  createdAt: string;
  /** Recorded at face value although Safe Haven took less. */
  atFaceValue: boolean;
}

export interface PersonPurchaseRepair {
  fromStatement: number;
  atRate: number;
  /** Null when the statement could not be read. */
  statementLines: number | null;
  rows: PersonPurchase[];
}

export interface PeopleRepairReport {
  repairs: LedgerRepairView[];
  outstanding: number;
  repaired: number;
}

export const consumerReconciliationApi = {
  purchases: async (userId: string) => {
    const { data } = await apiClient.get<ApiResponse<PersonPurchase[]>>(
      `/admin/consumer-reconciliation/users/${userId}/purchases`,
    );
    return data.data;
  },

  repairPurchases: async (userId: string) => {
    const { data } = await apiClient.post<ApiResponse<PersonPurchaseRepair>>(
      `/admin/consumer-reconciliation/users/${userId}/purchases/repair`,
    );
    return data.data;
  },

  repairs: async () => {
    const { data } = await apiClient.get<ApiResponse<PeopleRepairReport>>(
      '/admin/consumer-reconciliation/repairs',
    );
    return data.data;
  },

  runRepair: async (key: string) => {
    const { data } = await apiClient.post<ApiResponse<PeopleRepairReport>>(
      `/admin/consumer-reconciliation/repairs/${key}`,
    );
    return data.data;
  },
};
